<template>
  <div>
    <ts-page-title
      :title="$t('dailyAttendances.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('dailyAttendances.pageTitle'),
          active: true
        }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper
        class="ts-flex ts-flex-col lg:ts-flex-row lg:ts-justify-between ts-gap-4"
      >
        <div class="ts-flex ts-flex-wrap ts-gap-4">
          <!-- Date Picker -->
          <div class="ts-flex ts-flex-col ts-w-full md:ts-w-64">
            <label class="form-col-label control-label required ts-mb-2">
              {{ $t('dailyAttendances.date') }}
            </label>
            <DatePicker
              type="daterange"
              v-model="dateRange"
              placement="bottom-start"
              :placeholder="$t('dailyAttendances.date')"
              style="width: 100%"
              :transfer="true"
              format="dd-MM-yyyy"
              @on-change="onChangeDailyAttendanceDate"
            />
          </div>

          <!-- Org Structure -->
          <div class="ts-flex ts-flex-col ts-w-full md:ts-w-40">
            <label class="form-col-label control-label ts-mb-2">
              {{ $t('dailyAttendances.organisationalStructure') }}
            </label>
            <Select
              v-model="org_chart_structure_id"
              clearable
              placeholder="All"
            >
              <Option
                v-for="(orgChartStructure, index) in orgChartStructures"
                :key="index"
                :value="orgChartStructure.org_chart_structure_id"
              >
                {{ orgChartStructure.org_chart_structure }}
              </Option>
            </Select>
          </div>

          <!-- Status -->
          <div class="ts-flex ts-flex-col ts-w-full md:ts-w-32">
            <label class="form-col-label control-label ts-mb-2">
              {{ $t('dailyAttendances.status') }}
            </label>
            <Select v-model="status" multiple placeholder="All">
              <Option value="Present">Present</Option>
              <Option value="Absent">Absent</Option>
              <Option value="Take Leave">Take Leave</Option>
              <Option value="Day-Off">Day-Off</Option>
            </Select>
          </div>
          <div class="ts-flex ts-flex-col ts-w-full md:ts-w-40">
            <label class="form-col-label control-label ts-mb-2">
              {{ $t('dailyAttendances.position') }}
            </label>
            <Select
              v-model="position_id"
              clearable
              multiple
              placeholder="All"
            >
              <Option
                v-for="(position, index) in positions"
                :key="index"
                :value="position.position_id"
              >
                {{ position.position_name_en }}
              </Option>
            </Select>
          </div>

          <!-- Buttons -->
          <div class="ts-flex ts-items-end ts-gap-2 ts-w-full md:ts-w-auto">
            <ts-button
              outline
              color="primary"
              @click="fetchData({ refresh: false })"
            >
              {{ $t('view') }}
            </ts-button>

            <ts-button
              outline
              color="info"
              ghost
              @click="fetchData({ refresh: true })"
            >
              {{ $t('refresh') }}
            </ts-button>

            <ts-button
              @click.prevent="generateReport"
              :waiting="generating"
              outline
              color="info"
            >
              {{ $t('generate') }}
            </ts-button>

            <ts-button
              outline
              color="success"
              class="btn-block"
              @click.prevent="exportExcel"
              :waiting="exporting"
              ><i class="far fa-file-excel"></i> {{ $t('export') }}</ts-button
            >
          </div>
        </div>

        <div class="ts-w-full lg:ts-w-auto lg:ts-flex lg:ts-items-end">
          <Input
            search
            v-model="search"
            :placeholder="$t('employee.search')"
            class="ts-w-full md:ts-w-72"
            @on-focus="() => (width = 'width: 500px')"
            @on-blur="() => (width = 'width: 300px')"
          />
        </div>
      </ts-panel-wrapper>

      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        max-height="700"
        highlight-row
        stripe
        border
      >
        <template slot-scope="{ row }" slot="att_status">
          <span
            class="badge"
            :class="{
              'bg-success': row.att_status === 'Present',
              'bg-danger': row.att_status === 'Absent',
              'bg-warning': row.att_status === 'Take Leave',
              'bg-indigo': row.att_status === 'Day-Off'
            }"
            >{{ row.att_status }}</span
          >
        </template>

        <template slot-scope="{ row }" slot="att_present_status">
          <span
            v-if="row.att_present_status"
            :class="{
              'text-warning': row.att_present_status === 'Come Late',
              'text-pink': row.att_present_status === 'Early Leave',
              'text-success': row.att_present_status === 'On Time'
            }"
          >
            {{ row.att_present_status }}
          </span>
        </template>

        <template slot-scope="{ row }" slot="first_late">
          {{ row.first_late }}
        </template>

        <template slot-scope="{ row }" slot="att_present_status_2">
          <span
            v-if="row.att_present_status_2"
            :class="{
              'text-warning': row.att_present_status_2 === 'Come Late',
              'text-pink': row.att_present_status_2 === 'Early Leave',
              'text-success': row.att_present_status_2 === 'On Time'
            }"
          >
            {{ row.att_present_status_2 }}
          </span>
        </template>

        <template slot-scope="{ row }" slot="second_late">
          {{ row.second_late }}
        </template>

        <template slot-scope="{ row }" slot="att_present_status_3">
          <span
            v-if="row.att_present_status_3"
            :class="{
              'text-warning': row.att_present_status_3 === 'Come Late',
              'text-pink': row.att_present_status_3 === 'Early Leave',
              'text-success': row.att_present_status_3 === 'On Time'
            }"
          >
            {{ row.att_present_status_3 }}
          </span>
        </template>

        <template slot-scope="{ row }" slot="third_late">
          {{ row.third_late }}
        </template>

        <template slot-scope="{ row }" slot="t1">
          <strong>{{ row.t1 ? formatTime(row.t1) : '--:--' }}</strong>
          <small>
            / {{ row.first_in ? formatTime(row.first_in) : '--:--' }}</small
          >
        </template>

        <template slot-scope="{ row }" slot="t2">
          <strong>{{ row.t2 ? formatTime(row.t2) : '--:--' }}</strong>
          <small>
            / {{ row.first_out ? formatTime(row.first_out) : '--:--' }}</small
          >
        </template>

        <template slot-scope="{ row }" slot="t3">
          <strong>{{ row.t3 ? formatTime(row.t3) : '--:--' }}</strong>
          <small>
            / {{ row.second_in ? formatTime(row.second_in) : '--:--' }}</small
          >
        </template>

        <template slot-scope="{ row }" slot="t4">
          <strong>{{ row.t4 ? formatTime(row.t4) : '--:--' }}</strong>
          <small>
            / {{ row.second_out ? formatTime(row.second_out) : '--:--' }}</small
          >
        </template>

        <!-- T3 check-in / check-out slots -->
        <template slot-scope="{ row }" slot="t5">
          <strong>{{ row.t5 ? formatTime(row.t5) : '--:--' }}</strong>
          <small>
            / {{ row.third_in ? formatTime(row.third_in) : '--:--' }}</small
          >
        </template>

        <template slot-scope="{ row }" slot="t6">
          <strong>{{ row.t6 ? formatTime(row.t6) : '--:--' }}</strong>
          <small>
            / {{ row.third_out ? formatTime(row.third_out) : '--:--' }}</small
          >
        </template>

        <template slot-scope="{ row }" slot="employee_name">
          <strong>{{
            locale === 'kh'
              ? row.employee_name_kh
                ? row.employee_name_kh
                : row.employee_name_en
              : row.employee_name_en
          }}</strong>
          {{ row.card_id ? '(' + row.card_id + ')' : '' }}
        </template>

        <template slot-scope="{ row }" slot="total_hour1">
          <strong class="text-primary">{{ row.total_hour1 }}</strong>
        </template>
        <template slot-scope="{ row }" slot="total_hour2">
          <strong class="text-primary">{{ row.total_hour2 }}</strong>
        </template>
        <template slot-scope="{ row }" slot="total_hour3">
          <strong class="text-primary">{{ row.total_hour3 }}</strong>
        </template>
        <template slot-scope="{ row }" slot="total_hour4">
          <strong class="text-primary">{{ row.total_hour4 }}</strong>
        </template>
      </Table>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
    </ts-panel>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import { debounce } from 'lodash'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'daily-attendanceIndex',
  data() {
    return {
      dateRange: [
        moment()
          .add('days', -7)
          .format('DD-MM-YYYY'),
        moment().format('DD-MM-YYYY')
      ],
      errors: new Errors(),
      loading: false,
      org_chart_structure_id: null,
      position_id: [],
      status: [],
      data: null,
      width: 'width: 300px',
      exporting: false,
      generating: false
    }
  },
  computed: {
    ...mapState('humanResource/dailyAttendance', [
      'resources',
      'pagination',
      'orgChartStructures',
      'positions'
    ]),
    locale() {
      return this.$root.$i18n.locale
    },
    search: {
      get() {
        return this.$store.state.humanResource.dailyAttendance.search
      },
      set(newValue) {
        this.$store.commit('humanResource/dailyAttendance/SET_SEARCH', newValue)
        this.$store.commit('humanResource/dailyAttendance/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          type: 'index',
          width: 60,
          align: 'center'
        },
        {
          title: this.$t('dailyAttendances.branch'),
          key: 'branch',
          minWidth: 150
        },
        {
          title: this.$t('dailyAttendances.employeeName'),
          slot: 'employee_name',
          minWidth: 200
        },
        {
          title: this.$t('dailyAttendances.shift'),
          key: 'shift_name',
          minWidth: 150
        },
        {
          title: this.$t('dailyAttendances.position'),
          key: 'position',
          minWidth: 250
        },
        {
          title: this.$t('dailyAttendances.date'),
          key: 'datescan',
          minWidth: 150,
          align: 'center'
        },
        {
          title: this.$t('dailyAttendances.attStatus'),
          slot: 'att_status',
          minWidth: 150,
          align: 'center'
        },
        {
          title: this.$t('dailyAttendances.t1'),
          align: 'center',
          children: [
            {
              title: this.$t('dailyAttendances.checkIn'),
              align: 'center',
              slot: 't1',
              minWidth: 150
            },
            {
              title: this.$t('dailyAttendances.checkOut'),
              align: 'center',
              slot: 't2',
              minWidth: 150
            },
            {
              title: this.$t('dailyAttendances.total1'),
              slot: 'total_hour1',
              align: 'center',
              width: 100
            },
            {
              title: this.$t('dailyAttendances.attStatus'),
              slot: 'att_present_status',
              minWidth: 150,
              align: 'center'
            },
            {
              title: this.$t('dailyAttendances.minute'),
              slot: 'first_late',
              minWidth: 150,
              align: 'center'
            }
          ]
        },
        {
          title: this.$t('dailyAttendances.t2'),
          align: 'center',
          children: [
            {
              title: this.$t('dailyAttendances.checkIn'),
              align: 'center',
              slot: 't3',
              minWidth: 150
            },
            {
              title: this.$t('dailyAttendances.checkOut'),
              align: 'center',
              slot: 't4',
              minWidth: 150
            },
            {
              title: this.$t('dailyAttendances.total2'),
              slot: 'total_hour2',
              align: 'center',
              width: 100
            },
            {
              title: this.$t('dailyAttendances.attStatus'),
              slot: 'att_present_status_2',
              minWidth: 150,
              align: 'center'
            },
            {
              title: this.$t('dailyAttendances.minute'),
              slot: 'second_late',
              minWidth: 150,
              align: 'center'
            }
          ]
        },
        {
          title: this.$t('dailyAttendances.t3'),
          align: 'center',
          children: [
            {
              title: this.$t('dailyAttendances.checkIn'),
              align: 'center',
              slot: 't5',
              minWidth: 150
            },
            {
              title: this.$t('dailyAttendances.checkOut'),
              align: 'center',
              slot: 't6',
              minWidth: 150
            },
            {
              title: this.$t('dailyAttendances.total3'),
              slot: 'total_hour3',
              align: 'center',
              width: 100
            },
            {
              title: this.$t('dailyAttendances.attStatus'),
              slot: 'att_present_status_3',
              minWidth: 150,
              align: 'center'
            },
            {
              title: this.$t('dailyAttendances.minute'),
              slot: 'third_late',
              minWidth: 150,
              align: 'center'
            }
          ]
        },
        {
          title: this.$t('dailyAttendances.scannedOtherBranch'),
          key: 'scanned_other_branch',
          align: 'center',
          width: 200
        }
      ]
    }
  },
  methods: {
    ...mapActions('humanResource/dailyAttendance', [
      'getOrgChartStructure',
      'getPositions'
    ]),
    async fetchResource() {
      this.loading = true
      await this.getOrgChartStructure()
      await this.getPositions()
      this.loading = false
    },
    onChangeDailyAttendanceDate(dateRange) {
      this.dateRange = dateRange
      this.fetchData({
        dateRange: this.dateRange,
        org_chart_structure_id: this.org_chart_structure_id,
        position_id: this.position_id,
        status: this.status
      })
    },
    async fetchData(attributes) {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch('humanResource/dailyAttendance/fetch', {
          dateRange: this.dateRange,
          org_chart_structure_id: this.org_chart_structure_id,
          position_id: this.position_id,
          status: this.status,
          ...attributes
        })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()

      this.$store
        .dispatch('humanResource/dailyAttendance/exportExcel', {
          dateRange: this.dateRange,
          org_chart_structure_id: this.org_chart_structure_id,
          position_id: this.position_id,
          status: this.status
        })
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          console.log(err)
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    generateReport() {
      this.generating = true
      this.$store
        .dispatch('humanResource/dailyAttendance/generateReport')
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.generating = false
        })
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'DAILY ATTENDANCES',
        desc: not.text
      })
    },
    formatTime(time) {
      if (!time || time === '--:--') return '--:--'
      const [hours, minutes] = time.split(':')
      const h = parseInt(hours)
      const ampm = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${h12}:${minutes} ${ampm}`
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('humanResource/dailyAttendance/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchResource()
    })
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500),

    async org_chart_structure_id(newVal) {
      this.position_id = []
      this.$store.commit('humanResource/dailyAttendance/SET_POSITION', [])
      if (newVal) {
        await this.getPositions({ org_chart_structure_id: newVal })
      }
    }
  }
}
</script>
